import {
  ensureTrailingSlash,
  generatePaginationSuffix,
} from "@voxmedia/duet-utils/route";

import useRouteInfo from "./use-route-info";

type Args<X extends number | undefined> = {
  totalPages?: X;
  totalPagesLimit?: number;
  firstPageAsIndex?: boolean;
  hasNextPage?: boolean;
};

type Result<X extends number | undefined> = {
  pageNumber: number;
  firstPage: string;
  updatedTotalPages: X;
  currentPageHref: string | undefined;
  previousPageHref: string | undefined;
  nextPageHref: string | undefined;
  isPaginating: boolean;
};

export default function usePaginationDetails<T extends number | undefined>(
  args: Args<T>,
): Result<T> {
  const {
    totalPages,
    totalPagesLimit = Number.MAX_SAFE_INTEGER,
    firstPageAsIndex = true,
    hasNextPage = true,
  } = args;
  const route = useRouteInfo();

  const pageNumber = route.page || 1;

  const firstPage = firstPageAsIndex
    ? route.base
    : `${ensureTrailingSlash(route.base)}${generatePaginationSuffix(1)}`;

  const updatedTotalPages = totalPages
    ? Math.min(totalPagesLimit, totalPages)
    : undefined;

  const currentPageHref =
    route.page !== null
      ? pageNumber === 1
        ? firstPage
        : `${ensureTrailingSlash(route.base)}${generatePaginationSuffix(
            pageNumber,
          )}`
      : undefined;

  const previousPageHref =
    pageNumber > 1
      ? pageNumber === 2
        ? firstPage
        : `${ensureTrailingSlash(route.base)}${generatePaginationSuffix(
            pageNumber - 1,
          )}`
      : undefined;

  const nextPageHref = hasNextPage
    ? `${ensureTrailingSlash(route.base)}${generatePaginationSuffix(
        pageNumber + 1,
      )}`
    : undefined;

  return {
    pageNumber,
    firstPage,
    updatedTotalPages: updatedTotalPages as Result<T>["updatedTotalPages"],
    currentPageHref,
    previousPageHref,
    nextPageHref,
    isPaginating: route.page !== null,
  };
}
